<template>
	<BaseLayout :content-full-height="true" :footer-full="true" :include-padding="false" :transparent-header="true">
		<MarketingHeroImage :hero="hero" theme="dark">
			<template #eyebrow>
				<span class="overline text-color-gray-40">Introducing</span>
			</template>
			<template #cta>
				<BaseCtaLink
					classes="mt-200"
					icon="chevron"
					icon-dir="down"
					icon-size="24"
					:is-orange="true"
					:link="seeTheDocsLink"
				/>
			</template>
		</MarketingHeroImage>
		<WhyFundriseConnectApi />
		<ConnectApiWhyAlternatives />
		<WhyFundriseSection :data="whyFundriseContent" alignment="left" />
		<ConnectApiForDevelopers id="for-developers" />
		<OurBankAndRealEstatePartnersSection :section-text="bankAndRspSectionText" class="pt-500-md pt-300" />
	</BaseLayout>
</template>

<script setup lang="ts">
import { Link, MarketingImageHero } from 'types/layout';
import { appCloudfrontPath } from '@constants';
import ConnectApiForDevelopers from '@components/marketing-site/connect-api/connect-api-for-developers.vue';
import ConnectApiWhyAlternatives from '@components/marketing-site/connect-api/connect-api-why-alternatives.vue';
import MarketingHeroImage from '@components/marketing-site/marketing-hero-image.vue';
import OurBankAndRealEstatePartnersSection from '@components/marketing-site/private-credit/our-banking-and-real-estate-partners-section.vue';
import { ref } from 'vue';
import { useAppStore } from '@stores/app';
import { useHead } from '@unhead/vue';
import WhyFundriseConnectApi from '@components/marketing-site/connect-api/why-fundrise-connect-api.vue';
import WhyFundriseSection from '@components/marketing-site/why-fundrise/why-fundrise-section.vue';
import { WhyFundriseSectionData } from 'types/marketing-site';

const appStore = useAppStore();

const seeTheDocsLink: Link = {
	text: 'See the documentation',
	href: '#for-developers'
};

const whyFundriseContent: WhyFundriseSectionData = {
	theme: 'dark',
	video: {
		diagram: `${appCloudfrontPath}/video/why-fundrise-diagram.mp4`,
		diagramAnimation: `${appCloudfrontPath}/video/lottie-json/fundrise-e2e-testing.json`,
		diagramAnimationMobile: `${appCloudfrontPath}/video/lottie-json/fundrise-e2e-testing_mobile.json`,
		diagramMobile: `${appCloudfrontPath}/video/why-fundrise-diagram-mobile.mp4`,
		fallbackImage: `${appCloudfrontPath}/images/marketing-site/why-fundrise-fallback-image.png`,
		fallbackImageMobile: `${appCloudfrontPath}/images/marketing-site/why-fundrise-fallback-image-mobile.png`
	},
	diagramSubtext: `Fundrise's history of regulatory and technological innovation gives us a unique position to offer The Connect API.`,
	header: {
		title: 'Why Fundrise',
		heading: 'Leverage our billion-dollar scale and technology'
	},
	content: [
		`Fundrise is one of the first and largest, technology enabled alternative investment platforms in the world and from day one was designed specifically to serve individual investors. We manage more than $7 billion worth of assets and serve more than 2 million people. We've also engineered the industry's first end-to-end, fully integrated technology platform — a proprietary software system that manages everything from investor servicing to fund management and asset management.`,
		`The Fundrise Connect API is both a first-of-its-kind and the culmination of over a decade of enmeshing industry-leading technology with world-class asset management.`
	],
	cta: {
		text: 'Learn about our technology',
		router: 'why-fundrise'
	}
};

const bankAndRspSectionText =
	'We have worked with a majority of the top banks, sponsors, and capital brokers in the market, establishing a reputation as a good partner.';

const hero = ref<MarketingImageHero>({
	description: `The Fundrise Connect API gives financial platforms and investment apps the ability to natively offer access to institutional-grade, private-market alternative&nbsp;investments.`,
	imageUrl: '/images/marketing-site/connect-api-hero.png',
	title: appStore.isMobile ? 'Fundrise Connect&nbsp;API' : `Fundrise<br>Connect&nbsp;API`
});

function setup(): void {
	useHead({
		title: 'Connect API',
		meta: [
			{ property: 'og:title', content: 'Fundrise Connect API' },
			{
				name: 'description',
				content:
					'The Fundrise Connect API gives financial platforms and apps the ability to natively offer access to institutional-grade, private-market investments.'
			},
			{
				property: 'og:description',
				content:
					'The Fundrise Connect API gives financial platforms and apps the ability to natively offer access to institutional-grade, private-market investments.'
			},
			{
				property: 'og:image',
				content: `${appCloudfrontPath}/images/marketing-site/connect-api-meta-thumbnail.png`
			}
		]
	});
}

setup();
</script>
